import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import HeirloomView from '@/components/HeirloomView';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SampleHeirloom() {
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSample();
  }, []);

  const fetchSample = async () => {
    try {
      const { data } = await axios.get(`${API}/heirloom/sample/data`);
      setProject(data);
    } catch {
      setProject(null);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FCFBF9] flex items-center justify-center">
        <div className="text-center animate-fade-in">
          <div className="w-8 h-8 border border-[#A89F91] border-t-transparent rounded-full animate-spin mx-auto mb-6" />
          <p className="text-sm text-[#A89F91] tracking-widest uppercase">Opening the archive</p>
        </div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div data-testid="sample-heirloom-page">
      {/* Sample Banner */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-[#F2EFE9] border-b border-[#E7E5DF] px-4 py-2.5 flex items-center justify-center gap-4" data-testid="sample-banner">
        <p className="text-sm text-[#57534E]">
          <span className="font-medium text-[#1C1917]">Sample Heirloom</span> — This is what your family will receive
        </p>
        <Link to="/contact" data-testid="sample-cta" className="text-xs bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-4 py-1.5 transition-colors shrink-0">
          Start Yours
        </Link>
      </div>
      <div className="pt-10">
        <SampleHeirloomView project={project} />
      </div>
    </div>
  );
}

function SampleHeirloomView({ project }) {
  const [heroVisible, setHeroVisible] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    const timer = setTimeout(() => setHeroVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="heirloom-page">
      {/* Hero */}
      <section className="relative min-h-screen flex items-end" data-testid="heirloom-hero">
        <div className="absolute inset-0">
          <img
            src={project.hero_image?.url || "https://images.unsplash.com/photo-1622180458076-94175a4ed1d5?w=1200&q=80"}
            alt={project.loved_one_name}
            className={`w-full h-full object-cover transition-opacity duration-[2000ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1C1917] via-[#1C1917]/40 to-[#1C1917]/10" />
        </div>
        <div className={`relative z-10 max-w-4xl mx-auto px-6 sm:px-12 pb-20 sm:pb-28 w-full transition-all duration-[1500ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <p className="overline text-[#A89F91]/80 mb-6 tracking-[0.25em]">Their Legacy</p>
          <h1 className="font-['Cormorant_Garamond'] text-5xl sm:text-6xl lg:text-8xl font-medium text-[#FCFBF9] leading-[1.02] mb-6" data-testid="heirloom-loved-one-name">
            {project.loved_one_name}
          </h1>
          <div className="w-16 h-[1px] bg-[#A89F91]/40 mb-6" />
          <p className="font-['Cormorant_Garamond'] text-lg sm:text-xl italic text-[#A89F91]/90 max-w-md" data-testid="heirloom-tagline">
            {project.tagline}
          </p>
        </div>
      </section>

      {/* Story Film Placeholder */}
      <section className="py-20 sm:py-28 bg-[#1C1917]" data-testid="sample-story-film-section">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 text-center">
          <p className="overline text-[#A89F91] mb-3">Story Film</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#FCFBF9] mb-6">Their Professionally Edited Story Film</h2>
          <div className="aspect-video bg-[#2a2825] flex items-center justify-center border border-[#333]">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full border-2 border-[#A89F91]/40 flex items-center justify-center mx-auto mb-4">
                <svg width="20" height="24" viewBox="0 0 20 24" fill="none"><path d="M0 0L20 12L0 24V0Z" fill="#A89F91" fillOpacity="0.6"/></svg>
              </div>
              <p className="text-sm text-[#A89F91]/60">Story film plays here</p>
              <p className="text-xs text-[#78716C]/40 mt-1">Professionally edited from the guided interview</p>
            </div>
          </div>
        </div>
      </section>

      {/* Voice Archive Placeholder */}
      <section className="py-20 sm:py-28" data-testid="sample-voice-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <div className="mb-12">
            <p className="overline mb-3">Voice Archive</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917]">Hear Their Voice</h2>
          </div>
          <div className="border border-[#E7E5DF] p-6 sm:p-8 bg-white">
            <div className="flex items-center gap-5 py-2">
              <div className="w-14 h-14 flex items-center justify-center border border-[#D6D3CD] text-[#A89F91]">
                <svg width="16" height="20" viewBox="0 0 16 20" fill="none"><path d="M0 0L16 10L0 20V0Z" fill="currentColor"/></svg>
              </div>
              <div className="flex-1 space-y-1">
                <div className="w-full h-[3px] bg-[#E7E5DF] rounded-full"><div className="h-full w-[35%] bg-[#A89F91] rounded-full" /></div>
                <div className="flex justify-between"><span className="text-[11px] text-[#A89F91] font-mono">1:24</span><span className="text-[11px] text-[#A89F91] font-mono">4:08</span></div>
              </div>
            </div>
            <p className="text-xs text-[#A89F91] mt-3 italic">Audio recording from Eleanor's guided interview session</p>
          </div>
        </div>
      </section>

      {/* Photo Gallery */}
      <section className="py-20 sm:py-28 bg-[#F2EFE9]" data-testid="sample-gallery-section">
        <div className="max-w-6xl mx-auto px-6 sm:px-12">
          <div className="mb-12">
            <p className="overline mb-3">Photo Gallery</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917]">Moments Preserved</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
            {project.photos.map((photo, i) => (
              <button key={i} onClick={() => setLightboxIndex(i)} data-testid={`gallery-photo-${i}`} className="aspect-[4/3] overflow-hidden group">
                <img src={photo.url} alt={`Memory ${i + 1}`} className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.03] group-hover:opacity-90" />
              </button>
            ))}
          </div>
        </div>
        {lightboxIndex >= 0 && (
          <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center animate-fade-in" onClick={() => setLightboxIndex(-1)}>
            <button onClick={() => setLightboxIndex(-1)} className="absolute top-6 right-6 text-white/60 hover:text-white z-50">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <img src={project.photos[lightboxIndex]?.url} alt="" className="max-h-[85vh] max-w-[90vw] object-contain" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </section>

      {/* Life Lessons */}
      <section className="py-20 sm:py-28" data-testid="sample-lessons-section">
        <div className="max-w-3xl mx-auto px-6 sm:px-12">
          <div className="mb-14">
            <p className="overline mb-3">Life Lessons</p>
            <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-medium text-[#1C1917]">Words of Wisdom</h2>
          </div>
          <div className="space-y-12">
            {project.life_lessons.map((lesson, i) => (
              <div key={i} data-testid={`life-lesson-${i}`} className="border-l border-[#A89F91]/50 pl-8 sm:pl-10">
                <p className="font-['Cormorant_Garamond'] text-xl sm:text-2xl lg:text-3xl italic text-[#1C1917] leading-relaxed mb-3">"{lesson.text}"</p>
                {lesson.attribution && <p className="text-sm text-[#78716C]">— {lesson.attribution}</p>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 sm:py-28 bg-[#F2EFE9] text-center" data-testid="sample-cta-section">
        <div className="max-w-2xl mx-auto px-6 sm:px-12">
          <h2 className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl lg:text-5xl font-medium text-[#1C1917] leading-tight mb-6">
            Imagine this for your family.
          </h2>
          <p className="text-base sm:text-lg text-[#57534E] leading-relaxed mb-10">Every heirloom is unique — crafted around your loved one's voice, stories, and life.</p>
          <Link to="/contact" data-testid="sample-start-cta" className="inline-flex items-center justify-center bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] px-10 py-4 text-sm font-medium tracking-wide transition-all duration-300 gap-2">
            Start Your Family's Heirloom
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-20 sm:py-24 bg-[#1C1917] text-center">
        <div className="max-w-md mx-auto px-6">
          <div className="w-12 h-[1px] bg-[#A89F91]/30 mx-auto mb-8" />
          <p className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl italic text-[#A89F91]/80 leading-relaxed mb-8">"This is their story.<br />Keep it close."</p>
          <p className="text-[11px] text-[#78716C]/60 tracking-widest uppercase">Sample — Heirloom Stories Studio</p>
        </div>
      </footer>
    </div>
  );
}
